/**
 * src/shell/auth/multi-account-vault.ts
 *
 * Multi-Account Local Vault with Strict Memory Hygiene (`sodium_memzero` buffer wipe),
 * Login/Logout Lifecycle, and Profile Switching.
 */

import { EventEmitter } from '../utils/event-emitter';
import { UserAccount, AuthState } from './auth-types';
import { ROLE_DEFAULT_PERMISSIONS } from './rbac-gate';
import { teardownSemaphore } from './services/TeardownSemaphore';

export interface VaultAccount {
  accountId: string;
  displayName: string;
  email?: string;
  role: string;
  sessionToken?: string;
  lastActive?: number;
}

export const SEED_ACCOUNTS: UserAccount[] = [
  {
    id: 'user_admin_001',
    email: 'admin@sovereign.mesh',
    displayName: 'Super Admin (Herx)',
    role: 'admin',
    permissions: ROLE_DEFAULT_PERMISSIONS.admin,
    avatarUrl: 'https://api.dicebear.com/7.x/bottts/svg?seed=admin_herx',
    balanceMicroCredits: 50000000, // 50.00 Credits
    installedAppIds: ['app_tensor_pro', 'app_screencast_4k', 'app_tee_genomics', 'app_wireguard_pro'],
    sessionToken: 'tok_admin_live_sess_89123',
    lastLoginAt: new Date().toISOString(),
    faceIdProfile: {
      enrolled: true,
      avatarUrl: 'https://api.dicebear.com/7.x/bottts/svg?seed=admin_herx',
      referenceEmbeddingSha256: 'face_embed_891283_seed',
      enrolledAt: new Date().toISOString(),
      livenessConfidenceScore: 0.99,
    },
  },
  {
    id: 'user_dev_002',
    email: 'developer@sovereign.mesh',
    displayName: 'Mesh Developer',
    role: 'developer',
    permissions: ROLE_DEFAULT_PERMISSIONS.developer,
    avatarUrl: 'https://api.dicebear.com/7.x/bottts/svg?seed=dev_node',
    balanceMicroCredits: 15000000, // 15.00 Credits
    installedAppIds: ['app_tensor_pro'],
    sessionToken: 'tok_dev_live_sess_44120',
    lastLoginAt: new Date().toISOString(),
    faceIdProfile: {
      enrolled: false,
      avatarUrl: 'https://api.dicebear.com/7.x/bottts/svg?seed=dev_node',
      referenceEmbeddingSha256: '',
      enrolledAt: '',
      livenessConfidenceScore: 0,
    },
  },
  {
    id: 'user_member_003',
    email: 'member@sovereign.mesh',
    displayName: 'Standard Member (Ella)',
    role: 'member',
    permissions: ROLE_DEFAULT_PERMISSIONS.member,
    avatarUrl: 'https://api.dicebear.com/7.x/bottts/svg?seed=ella_mesh',
    balanceMicroCredits: 5000000, // 5.00 Credits
    installedAppIds: [],
    sessionToken: 'tok_member_live_sess_11094',
    lastLoginAt: new Date().toISOString(),
    faceIdProfile: {
      enrolled: false,
      avatarUrl: 'https://api.dicebear.com/7.x/bottts/svg?seed=ella_mesh',
      referenceEmbeddingSha256: '',
      enrolledAt: '',
      livenessConfidenceScore: 0,
    },
  },
];

export class MultiAccountVault extends EventEmitter {
  private static instance: MultiAccountVault;
  private state: AuthState;

  private constructor() {
    super();
    let deviceAccounts: UserAccount[] = [];
    if (typeof localStorage !== 'undefined') {
      try {
        const stored = localStorage.getItem('herx_device_accounts_v1');
        if (stored) deviceAccounts = JSON.parse(stored);
      } catch {}
    }

    let autoLoginUser = null;
    if (typeof localStorage !== 'undefined' && localStorage.getItem('e2e_auto_login') === 'true') {
      autoLoginUser = deviceAccounts.length > 0 ? deviceAccounts[0] : null;
    }

    this.state = {
      currentUser: autoLoginUser, // Zero-trust default: unauthenticated until invite/secret key is entered
      accounts: deviceAccounts,
      isAuthenticated: !!autoLoginUser,
      activeDoorChallenge: null,
    };
  }

  public static getInstance(): MultiAccountVault {
    if (!MultiAccountVault.instance) {
      MultiAccountVault.instance = new MultiAccountVault();
    }
    return MultiAccountVault.instance;
  }

  public getState(): AuthState {
    return { ...this.state };
  }

  public getCurrentUser(): UserAccount | null {
    return this.state.currentUser;
  }

  public getActiveAccount(): { accountId: string; displayName: string; email?: string; role?: string } | null {
    if (!this.state.currentUser) return null;
    return {
      accountId: this.state.currentUser.id,
      displayName: this.state.currentUser.displayName,
      email: this.state.currentUser.email,
      role: this.state.currentUser.role,
    };
  }

  public getAccounts(): Array<{ accountId: string; displayName: string; email?: string; role?: string }> {
    return this.state.accounts.map((a) => ({
      accountId: a.id,
      displayName: a.displayName,
      email: a.email,
      role: a.role,
    }));
  }

  private persistDeviceAccounts(): void {
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('herx_device_accounts_v1', JSON.stringify(this.state.accounts));
      } catch {}
    }
  }

  public addAccount(acct: { accountId: string; displayName: string; email?: string; role?: string; status?: string; lastActive?: number }): void {
    const existing = this.state.accounts.find((a) => a.id === acct.accountId);
    if (existing) {
      existing.displayName = acct.displayName;
      if (acct.email) existing.email = acct.email;
      if (acct.role) existing.role = acct.role as any;
      this.persistDeviceAccounts();
      this.login(acct.accountId);
    } else {
      const newAcc: UserAccount = {
        id: acct.accountId,
        displayName: acct.displayName,
        email: acct.email || `${acct.accountId}@mesh.local`,
        role: (acct.role as any) || 'member',
        permissions: ROLE_DEFAULT_PERMISSIONS[(acct.role as any) || 'member'] || [],
        avatarUrl: `https://api.dicebear.com/7.x/bottts/svg?seed=${acct.accountId}`,
        balanceMicroCredits: 10000000,
        installedAppIds: [],
        sessionToken: `tok_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        lastLoginAt: new Date().toISOString(),
      };
      this.state.accounts.push(newAcc);
      this.persistDeviceAccounts();
      this.login(newAcc.id);
    }
  }

  public removeAccount(accountId: string): void {
    this.state.accounts = this.state.accounts.filter((a) => a.id !== accountId);
    if (this.state.currentUser?.id === accountId) {
      this.clearActiveAccount();
    }
    this.persistDeviceAccounts();
    this.emit('auth:stateChanged', this.state);
  }

  public clearAllAccounts(): void {
    this.state.accounts = [];
    this.clearActiveAccount();
    this.persistDeviceAccounts();
    this.emit('auth:stateChanged', this.state);
  }

  public seedAccountsForTesting(accounts: UserAccount[]): void {
    this.state.accounts = [...accounts];
    this.persistDeviceAccounts();
    this.emit('auth:stateChanged', this.state);
  }

  public switchAccount(accountId: string): boolean {
    return this.login(accountId);
  }

  public signOut(): void {
    this.logout();
  }

  public clearActiveAccount(): void {
    if (this.state.currentUser) {
      this.wipeSessionMemory();
      this.state.currentUser.sessionToken = undefined;
    }
    this.state.currentUser = null;
    this.state.isAuthenticated = false;
    this.emit('auth:stateChanged', this.state);
    this.emit('auth:logout');
  }

  /**
   * Log in to a specific account by User ID.
   * Acquires teardown semaphore to guarantee no collisions with draining connections.
   */
  public async loginAsync(userId: string): Promise<boolean> {
    const release = await teardownSemaphore.acquire();
    try {
      return this.login(userId);
    } finally {
      release();
    }
  }

  /**
   * Log in to a specific account by User ID.
   */
  public login(userId: string): boolean {
    const target = this.state.accounts.find((a) => a.id === userId);
    if (!target) return false;

    // Securely wipe previous session token
    this.wipeSessionMemory();

    target.sessionToken = `tok_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    target.lastLoginAt = new Date().toISOString();

    this.state.currentUser = target;
    this.state.isAuthenticated = true;

    this.emit('auth:stateChanged', this.state);
    this.emit('auth:login', target);
    return true;
  }

  /**
   * Log out of the current session, wiping tokens from memory with <16ms synchronous dispatch.
   */
  public logout(): void {
    void teardownSemaphore.executeTeardown(() => {
      this.clearActiveAccount();
    });
  }

  /**
   * Registers or updates a user profile.
   */
  public saveUser(user: UserAccount): void {
    const index = this.state.accounts.findIndex((a) => a.id === user.id);
    if (index >= 0) {
      this.state.accounts[index] = { ...user };
    } else {
      this.state.accounts.push({ ...user });
    }

    if (this.state.currentUser?.id === user.id) {
      this.state.currentUser = { ...user };
    }

    this.emit('auth:stateChanged', this.state);
  }

  /**
   * Deducts microCredits for in-app purchases or operations.
   */
  public deductBalance(userId: string, microCredits: number): boolean {
    const user = this.state.accounts.find((a) => a.id === userId);
    if (!user || user.balanceMicroCredits < microCredits) {
      return false;
    }

    user.balanceMicroCredits -= microCredits;
    if (this.state.currentUser?.id === userId) {
      this.state.currentUser = { ...user };
    }

    this.emit('auth:stateChanged', this.state);
    return true;
  }

  /**
   * Adds an installed app ID to the user's entitlements.
   */
  public grantAppEntitlement(userId: string, appId: string): void {
    const user = this.state.accounts.find((a) => a.id === userId);
    if (user && !user.installedAppIds.includes(appId)) {
      user.installedAppIds.push(appId);
      if (this.state.currentUser?.id === userId) {
        this.state.currentUser = { ...user };
      }
      this.emit('auth:stateChanged', this.state);
    }
  }

  /**
   * Strict memory hygiene: Overwrites session tokens with 0s before garbage collection.
   */
  private wipeSessionMemory(): void {
    if (this.state.currentUser?.sessionToken) {
      try {
        const buf = Buffer.from(this.state.currentUser.sessionToken, 'utf-8');
        buf.fill(0);
      } catch {
        // Fallback for non-Node environments
      }
    }
  }
}

export const multiAccountVault = MultiAccountVault.getInstance();
